// src/pages/finances/TypesDepenses.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Row, Col, Card, Spinner, Modal, Alert, Button, Badge,
    Table, Pagination, Form, InputGroup
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-warning { border-left-color: ${COLORS.warning}; }
  .kpi-info { border-left-color: ${COLORS.info}; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-warning .kpi-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .kpi-info .kpi-icon { background: ${COLORS.info}15; color: ${COLORS.info}; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }
  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }

  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
  }
  .action-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.08);
  }
  .action-btn-edit { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }
  .action-btn-edit:hover { background: ${COLORS.secondary}; color: white; }
  .action-btn-delete { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .action-btn-delete:hover { background: ${COLORS.danger}; color: white; }

  .pagination-modern .page-link {
    border: none;
    margin: 0 2px;
    border-radius: 4px;
    color: #64748b;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
    transition: all 0.2s ease;
  }
  .pagination-modern .page-link:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(41,90,167,0.2);
  }
  .pagination-modern .page-item.active .page-link {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 4px 8px rgba(41,90,167,0.3);
  }

  .type-modal .modal-content,
  .delete-modal .modal-content {
    border: none;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
  .type-modal .modal-header,
  .delete-modal .modal-header {
    border-bottom: none;
    padding: 1.2rem 1.5rem 0.5rem;
  }
  .type-modal .modal-footer,
  .delete-modal .modal-footer {
    border-top: none;
    padding: 0.5rem 1.5rem 1.2rem;
  }

  .modal-icon-circle,
  .delete-icon-circle {
    width: 56px;
    height: 56px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }
  .modal-icon-circle {
    background: ${COLORS.primary}15;
    color: ${COLORS.primary};
    font-size: 1.8rem;
  }
  .delete-icon-circle {
    background: ${COLORS.danger}15;
    color: ${COLORS.danger};
    font-size: 1.8rem;
  }

  .input-icon-wrapper {
    position: relative;
  }
  .input-icon {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 1rem;
    z-index: 2;
  }
  .form-control-modern.with-icon {
    padding-left: 2.2rem;
  }
  .form-control-modern {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    background: white;
  }
  .form-control-modern:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(41,90,167,0.08);
  }
  .form-control-modern.is-invalid {
    border-color: ${COLORS.danger};
  }
  .form-control-modern.is-invalid:focus {
    box-shadow: 0 0 0 3px rgba(239,68,68,0.08);
  }

  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

// ===== UTILITAIRES =====
const truncateText = (text, maxLength = 20) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export default function TypesDepenses() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({ matricule: '', intitule: '' });
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setIsAdmin(user.role === 'Administrateur' || user.role === 'Super Administrateur');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reload')) {
      setRefreshTrigger(prev => prev + 1);
      navigate('/finances/typesDepenses', { replace: true });
    }
  }, [location, navigate]);

  const fetchTypesDepenses = useCallback(async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/pages/signin2'); return; }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const timestamp = Date.now();
      let url = `https://josephsbusiness.com/api/type_depense.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const result = await response.json();

      if (result.success) {
        setData(result);
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, itemsPerPage, navigate]);

  useEffect(() => {
    fetchTypesDepenses();
  }, [fetchTypesDepenses, refreshTrigger]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateForm = () => {
    const errors = {};
    if (!typeForm.matricule.trim()) errors.matricule = 'Le matricule est requis';
    if (!typeForm.intitule.trim()) errors.intitule = "L'intitulé est requis";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTypeForm(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAddClick = () => {
    setEditingType(null);
    setTypeForm({ matricule: '', intitule: '' });
    setValidationErrors({});
    setShowTypeModal(true);
  };

  const handleEditClick = (type) => {
    setEditingType(type);
    setTypeForm({ matricule: type.matricule, intitule: type.intitule });
    setValidationErrors({});
    setShowTypeModal(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const url = `https://josephsbusiness.com/api/type_depense.php?user_id=${userId}`;
      const method = editingType ? 'PUT' : 'POST';
      const bodyData = editingType
        ? { id: editingType.id, intitule: typeForm.intitule }
        : { matricule: typeForm.matricule, intitule: typeForm.intitule };

      const response = await fetch(url, {
        method: method,
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData)
      });
      const result = await response.json();

      if (result.success) {
        setShowTypeModal(false);
        setRefreshTrigger(prev => prev + 1);
        setSuccessMessage(editingType ? 'Type de dépense modifié' : 'Type de dépense ajouté');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        alert(result.message || 'Erreur');
      }
    } catch (err) { alert('Erreur: ' + err.message); } finally { setSaving(false); }
  };

  const handleDeleteClick = (type) => {
    setTypeToDelete(type);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!typeToDelete) return;
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/type_depense.php?user_id=${userId}&id=${typeToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setTypeToDelete(null);
        setRefreshTrigger(prev => prev + 1);
        setSuccessMessage('Type de dépense supprimé');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        alert(result.message || 'Erreur');
      }
    } catch (err) { alert('Erreur: ' + err.message); } finally { setDeleting(false); }
  };

  const exportToExcel = () => {
    if (!data?.types) return;
    const fileName = `types_depenses_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = data.types.map(item => ({
      'Matricule': item.matricule,
      'Intitulé': item.intitule,
      'Date création': item.date_creation ? new Date(item.date_creation).toLocaleDateString('fr-FR') : '-'
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.sheet_add_aoa(ws, [
      [`Types de dépenses - ${new Date().toLocaleDateString('fr-FR')}`]
    ], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, "Types de dépenses");
    XLSX.writeFile(wb, fileName);
  };

  // ===== IMPRESSION PDF MODERNISÉE (iframe) =====
  const printPDF = () => {
    let egliseNom = 'JOSEPHSBUSINESS';
    let egliseLogo = 'logo.jpeg';

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        egliseNom = user.temple || user.nom_eglise || user.eglise_nom || 'JOSEPHSBUSINESS';
        egliseLogo = user.logo_parent || user.logo || 'logo.jpeg';
      }
    } catch (e) {}

    const types = data?.types || [];

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Types de dépenses - ${egliseNom}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; background: white; }
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 3px solid ${COLORS.primary};
            padding-bottom: 15px;
            margin-bottom: 20px;
            flex-wrap: wrap;
          }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 60px; width: auto; }
          .header-title .company-name {
            font-size: 20px;
            font-weight: 700;
            color: ${COLORS.primary};
          }
          .header-title .subtitle {
            color: #64748b;
            font-size: 14px;
          }
          .header-right { text-align: right; font-size: 13px; color: #64748b; }
          .summary {
            background: #f8fafc;
            padding: 12px 20px;
            border-left: 4px solid ${COLORS.secondary};
            margin-bottom: 20px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-around;
            gap: 10px;
          }
          .summary-item { font-size: 14px; }
          .summary-item strong { color: ${COLORS.primary}; }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            margin-top: 15px;
          }
          th {
            background: ${COLORS.primary};
            color: white;
            padding: 10px 8px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          td {
            padding: 8px;
            border-bottom: 1px solid #e2e8f0;
          }
          tr:nth-child(even) { background: #f8fafc; }
          .footer {
            margin-top: 25px;
            text-align: center;
            font-size: 12px;
            color: #94a3b8;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="https://josephsbusiness.com/assets/images/${egliseLogo}" alt="Logo" class="logo" onerror="this.style.display='none'">
            <div class="header-title">
              <div class="company-name">${egliseNom}</div>
              <div class="subtitle">Types de dépenses</div>
            </div>
          </div>
          <div class="header-right">
            <div><strong>Total</strong> : ${types.length}</div>
            <div style="font-size:12px;">${new Date().toLocaleDateString('fr-FR')}</div>
          </div>
        </div>

        <div class="summary">
          <span class="summary-item"><strong>Total</strong> : ${types.length}</span>
          <span class="summary-item"><strong>Affichés</strong> : ${types.length}</span>
          <span class="summary-item"><strong>Date</strong> : ${new Date().toLocaleDateString('fr-FR')}</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>Matricule</th>
              <th>Intitulé</th>
              <th>Date création</th>
            </tr>
          </thead>
          <tbody>
            ${types.map(type => `
              <tr>
                <td>${type.matricule}</td>
                <td>${truncateText(type.intitule, 30)}</td>
                <td>${type.date_creation ? new Date(type.date_creation).toLocaleDateString('fr-FR') : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Document généré automatiquement • ${new Date().toLocaleDateString('fr-FR')} • ${egliseNom}
        </div>
      </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    iframe.contentWindow.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1000);
    };
  };

  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="loading-wrapper">
            <div className="text-center">
              <div className="loading-ring-wrapper mb-3">
                <div className="loading-ring"></div>
                <div className="loading-ring-inner"></div>
              </div>
              <h5 className="fw-bold" style={{ color: COLORS.primaryDark }}>Chargement...</h5>
              <p className="text-muted small">Récupération des types de dépenses</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const types = data?.types || [];
  const pagination = data?.pagination || { total: types.length, pages: 1 };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    for (let i = start; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>);
    }
    if (end < pagination.pages) {
      if (end < pagination.pages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      items.push(<Pagination.Item key={pagination.pages} onClick={() => handlePageChange(pagination.pages)}>{pagination.pages}</Pagination.Item>);
    }
    return items;
  };

  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-price-tag-3-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Types de dépenses</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-bar-chart-line"></i> {pagination.total} types
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
              </Button>
              <Button size="sm" className="btn-modern-success" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </Button>
              <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                <i className="ri-file-pdf-line"></i> PDF
              </Button>
              {isAdmin && (
                <Button size="sm" className="btn-modern-primary" onClick={handleAddClick}>
                  <i className="ri-add-line"></i> Nouveau type
                </Button>
              )}
            </div>
          </div>

          {/* ===== MESSAGE DE SUCCÈS ===== */}
          {showSuccessMessage && (
            <Alert variant="success" className="border-0 shadow-sm rounded-3 mb-3" onClose={() => setShowSuccessMessage(false)} dismissible style={{ borderRadius: '4px' }}>
              <div className="d-flex align-items-center">
                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                <span>{successMessage}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-price-tag-3-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Types</span>
                  <h3 className="kpi-value">{pagination.total}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-list-check"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Affichés</span>
                  <h3 className="kpi-value">{types.length}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-info">
                <div className="kpi-icon"><i className="ri-building-2-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Temples</span>
                  <h3 className="kpi-value">-</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-warning">
                <div className="kpi-icon"><i className="ri-calendar-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Année</span>
                  <h3 className="kpi-value">{new Date().getFullYear()}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== FILTRES ===== */}
          {showFilters && (
            <Card className="filter-card mb-4 fade-in">
              <Card.Body className="p-3">
                <Form onSubmit={handleSearch}>
                  <Row className="g-2">
                    <Col md={10}>
                      <InputGroup size="sm">
                        <InputGroup.Text style={{ background: 'transparent', border: 'none', color: 'white' }}>
                          <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control placeholder="Matricule ou intitulé..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                      </InputGroup>
                    </Col>
                    <Col md={2}>
                      <Button type="submit" size="sm" className="btn-modern-primary w-100">
                        <i className="ri-filter-3-line me-1"></i> Filtrer
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* ===== TABLEAU ===== */}
          <Card className="table-card">
            <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2">
              <span className="fw-semibold"><i className="ri-list-check me-1"></i> Liste des types de dépenses</span>
              <div className="d-flex align-items-center gap-2">
                <small className="text-white-50">Lignes</small>
                <Form.Select size="sm" style={{ width: '65px', background: 'rgba(255,255,255,0.9)', border: 'none' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
            </div>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle table-modern">
                  <thead>
                    <tr>
                      <th className="px-2">Matricule</th>
                      <th className="px-2">Intitulé</th>
                      <th className="px-2">Date création</th>
                      <th className="px-2 text-center" style={{ width: '70px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.length > 0 ? (
                      types.map((type) => (
                        <tr key={type.id}>
                          <td className="px-2"><span className="reference-badge">{type.matricule}</span></td>
                          <td className="px-2">
                            <i className="ri-price-tag-3-line me-2" style={{ color: COLORS.primary }}></i>
                            {truncateText(type.intitule, 30)}
                          </td>
                          <td className="px-2">
                            <i className="ri-calendar-line me-2 text-muted"></i>
                            {type.date_creation ? new Date(type.date_creation).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-2 text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              {isAdmin && (
                                <>
                                  <button className="action-btn action-btn-edit" title="Modifier" onClick={() => handleEditClick(type)}>
                                    <i className="ri-edit-line"></i>
                                  </button>
                                  <button className="action-btn action-btn-delete" title="Supprimer" onClick={() => handleDeleteClick(type)}>
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </>
                              )}
                              {!isAdmin && <span className="text-muted small">Lecture seule</span>}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <i className="ri-inbox-line" style={{ fontSize: '2rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}></i>
                          <p className="text-muted small">Aucun type de dépense trouvé</p>
                          {isAdmin && (
                            <Button size="sm" className="btn-modern-primary" onClick={handleAddClick}>
                              <i className="ri-add-line"></i> Ajouter
                            </Button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            {pagination.pages > 1 && (
              <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Page {currentPage} / {pagination.pages}</small>
                  <Pagination className="pagination-modern mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                    <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>

          <Footer />
        </div>
      </div>

      {/* ===== MODAL D'AJOUT / MODIFICATION MODERNISÉ ===== */}
      <Modal show={showTypeModal} onHide={() => setShowTypeModal(false)} centered className="type-modal">
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}
        >
          <Modal.Title className="w-100 text-center">
            <div className="modal-icon-wrapper mb-2">
              <div className="modal-icon-circle" style={{
                width: '70px',
                height: '70px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <i className={editingType ? "ri-edit-line" : "ri-add-line"} style={{ fontSize: '32px', color: 'white' }}></i>
              </div>
            </div>
            <h5 className="fw-bold text-white mb-0">
              {editingType ? 'Modifier le type de dépense' : 'Ajouter un type de dépense'}
            </h5>
            <p className="text-white-50 small mb-0">
              {editingType ? 'Modifiez les informations' : 'Remplissez les informations'}
            </p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-4 px-4">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Matricule <span className="text-danger">*</span></Form.Label>
              <div className="input-icon-wrapper">
                <i className="ri-barcode-line input-icon"></i>
                <Form.Control
                  type="text"
                  name="matricule"
                  value={typeForm.matricule}
                  onChange={handleInputChange}
                  placeholder="Ex: DEP001"
                  isInvalid={!!validationErrors.matricule}
                  disabled={editingType}
                  className="form-control-modern with-icon"
                />
                {validationErrors.matricule && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {validationErrors.matricule}
                  </Form.Control.Feedback>
                )}
              </div>
              <Form.Text className="text-muted">Identifiant unique du type de dépense</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Intitulé <span className="text-danger">*</span></Form.Label>
              <div className="input-icon-wrapper">
                <i className="ri-price-tag-3-line input-icon"></i>
                <Form.Control
                  type="text"
                  name="intitule"
                  value={typeForm.intitule}
                  onChange={handleInputChange}
                  placeholder="Ex: Fournitures de bureau"
                  isInvalid={!!validationErrors.intitule}
                  className="form-control-modern with-icon"
                />
                {validationErrors.intitule && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {validationErrors.intitule}
                  </Form.Control.Feedback>
                )}
              </div>
              <Form.Text className="text-muted">Nom descriptif du type de dépense</Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button
            variant="light"
            onClick={() => setShowTypeModal(false)}
            disabled={saving}
            className="px-4"
            style={{
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              border: '1px solid #d1d5db',
              color: '#4b5563',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              background: 'white'
            }}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="px-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500',
              fontSize: '0.85rem',
              boxShadow: '0 4px 10px rgba(41, 90, 167, 0.25)',
              transition: 'all 0.2s',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 16px rgba(41, 90, 167, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 10px rgba(41, 90, 167, 0.25)';
            }}
          >
            {saving ? (
              <><Spinner size="sm" className="me-1" /> Sauvegarde...</>
            ) : (
              <><i className="ri-save-line me-2"></i> {editingType ? 'Modifier' : 'Ajouter'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== MODAL DE SUPPRESSION MODERNISÉ ===== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="delete-modal">
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}
        >
          <Modal.Title className="w-100 text-center">
            <div className="delete-icon-wrapper mb-2">
              <div className="delete-icon-circle" style={{
                width: '70px',
                height: '70px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
              </div>
            </div>
            <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
            <p className="text-white-50 small mb-0">Cette action est irréversible</p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-4 px-4">
          {typeToDelete && (
            <>
              <p className="text-center text-muted mb-3">
                Êtes-vous sûr de vouloir supprimer définitivement ce type de dépense ?
              </p>

              <div className="p-3 mb-3" style={{
                background: '#f8fafc',
                borderLeft: `3px solid ${COLORS.primary}`,
                borderRadius: '4px'
              }}>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small"><i className="ri-barcode-line me-2"></i>Matricule</span>
                    <span className="fw-semibold">{typeToDelete.matricule}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small"><i className="ri-price-tag-3-line me-2"></i>Intitulé</span>
                    <span className="fw-semibold">{typeToDelete.intitule}</span>
                  </div>
                </div>
              </div>

              <div style={{
                background: `${COLORS.danger}10`,
                border: `1px solid ${COLORS.danger}30`,
                borderRadius: '4px',
                padding: '0.8rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}>
                <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
                <span className="small text-muted">
                  La suppression de ce type de dépense pourrait affecter les dépenses existantes qui l'utilisent.
                </span>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
            className="px-4"
            style={{
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              border: '1px solid #d1d5db',
              color: '#4b5563',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              background: 'white'
            }}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="px-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500',
              fontSize: '0.85rem',
              boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
              transition: 'all 0.2s',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
            }}
          >
            {deleting ? (
              <><Spinner size="sm" className="me-1" /> Suppression...</>
            ) : (
              <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}